<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount, ref } from 'vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import V2CatalogActionList from '@/components/v2/catalog/panels/V2CatalogActionList.vue';
  import V2MenuPhotoSlot from '@/components/v2/menu-import/editor/V2MenuPhotoSlot.vue';
  import { useProductEditorContext } from '@/composition/catalog/use-product-editor';
  import { uploadMenuPhoto } from '@/composition/menu-photo-upload';
  import { useUi } from '@/composition/use-ui';
  import type { ICatalogMenuEntry } from '@/domain/catalog/catalog-menus';
  import type {
    ISizeRowErrors,
    ISizeRowForm,
  } from '@/domain/catalog/product-form';
  import { MAX_SIZE_TITLE_LENGTH } from '@/domain/catalog/product-form';

  /**
   * One size (plan §5.12; «variante» in the UI): «Variante» + «Precio» + ⋯
   * («Foto y descripción», «Precio con descuento», «Pausar esta variante»,
   * «Subir», «Bajar», «Quitar»). A paused size dims and says so. Its photo
   * pick is tracked by the editor: «Guardar» waits for it.
   */
  const props = defineProps<{
    row: ISizeRowForm;
    currencySymbol: string;
    errors?: ISizeRowErrors | null;
    first: boolean;
    last: boolean;
    readOnly: boolean;
    online: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'change', patch: Partial<ISizeRowForm>): void;
    (e: 'move', delta: -1 | 1): void;
    (e: 'remove'): void;
  }>();

  type TSizeAction =
    | 'details'
    | 'discount'
    | 'pause'
    | 'up'
    | 'down'
    | 'remove';

  const ui = useUi();
  const editor = useProductEditorContext();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const detailsOpen = ref(!!props.row.picture || !!props.row.description);
  const uploading = ref(false);
  let unmounted = false;
  onBeforeUnmount(() => {
    unmounted = true;
  });

  const ERRORS: Record<string, string> = {
    required: 'menuPage.price.required',
    invalid: 'menuPage.price.invalid',
    discountTooHigh: 'menuPage.price.discountTooHigh',
  };

  const titleError = computed(() => {
    const error = props.errors?.title;
    if (error === 'required') return t('menuPage.editor.errors.sizeTitle');
    if (error === 'tooLong')
      return t('menuPage.editor.errors.tooLong', { n: MAX_SIZE_TITLE_LENGTH });
    return null;
  });
  const priceError = computed(() =>
    props.errors?.price ? t(ERRORS[props.errors.price]) : null
  );
  const discountError = computed(() =>
    props.errors?.discount ? t(ERRORS[props.errors.discount]) : null
  );

  const entries = computed<ICatalogMenuEntry<TSizeAction>[]>(() => {
    const entry = (
      key: TSizeAction,
      labelKey: string,
      icon: string,
      danger = false
    ): ICatalogMenuEntry<TSizeAction> => ({
      key,
      labelKey,
      icon,
      danger,
      write: false,
      plan: null,
    });
    const list = [
      entry('details', 'menuPage.editor.size.details', 'image-outline'),
      entry(
        'discount',
        props.row.discountText === null
          ? 'menuPage.editor.size.addDiscount'
          : 'menuPage.editor.size.removeDiscount',
        'tag-outline'
      ),
      entry(
        'pause',
        props.row.active
          ? 'menuPage.editor.size.pause'
          : 'menuPage.editor.size.resume',
        props.row.active ? 'pause-circle-outline' : 'play-circle-outline'
      ),
    ];
    if (!props.first)
      list.push(entry('up', 'menuPage.editor.size.up', 'arrow-up'));
    if (!props.last)
      list.push(entry('down', 'menuPage.editor.size.down', 'arrow-down'));
    list.push(
      entry('remove', 'menuPage.editor.size.remove', 'delete-outline', true)
    );
    return list;
  });

  function onAction(key: string, close: () => void) {
    close();
    if (key === 'details') detailsOpen.value = !detailsOpen.value;
    else if (key === 'discount')
      emit('change', {
        discountText: props.row.discountText === null ? '' : null,
      });
    else if (key === 'pause') emit('change', { active: !props.row.active });
    else if (key === 'up') emit('move', -1);
    else if (key === 'down') emit('move', 1);
    else if (key === 'remove') emit('remove');
  }

  async function uploadPhoto(file: File) {
    uploading.value = true;
    try {
      const path = await uploadMenuPhoto(file);
      if (!unmounted) emit('change', { picture: path });
    } catch (error) {
      if (!unmounted) ui.serverError(error, t('menuPage.errors.photo'));
    } finally {
      if (!unmounted) uploading.value = false;
    }
  }

  /** The photo is part of the next «Guardar» until it is in the form. */
  function pickPhoto(file: File) {
    editor.trackUpload(uploadPhoto(file));
  }
</script>

<template>
  <div class="v2-catalog-size-row" :class="{ 'is-paused': !row.active }">
    <div class="size-main">
      <div class="size-title">
        <PosUiInput
          :value="row.title"
          :maxlength="MAX_SIZE_TITLE_LENGTH"
          :placeholder="t('menuPage.editor.size.titlePlaceholder')"
          :aria-label="t('menuPage.editor.size.title')"
          :disabled="readOnly"
          @input="emit('change', { title: $event })"
        />
      </div>
      <div class="size-price">
        <PosUiInput
          :value="row.priceText"
          :prefix="currencySymbol"
          inputmode="decimal"
          autocomplete="off"
          :aria-label="t('menuPage.price.price')"
          :disabled="readOnly"
          @input="emit('change', { priceText: $event })"
        />
      </div>
      <PosUiPopover
        v-if="!readOnly"
        class="size-more"
        placement="bottom"
        align="end"
      >
        <template #trigger="{ toggle }">
          <button
            type="button"
            class="more-trigger"
            :aria-label="t('menuPage.editor.size.actions', { name: row.title })"
            @click="toggle"
          >
            <i class="mdi mdi-dots-horizontal" />
          </button>
        </template>
        <template #default="{ close }">
          <V2CatalogActionList
            class="more-list"
            :entries="entries"
            :online="online"
            @select="onAction($event, close)"
          />
        </template>
      </PosUiPopover>
    </div>

    <p v-if="!row.active" class="size-paused">
      {{ t('menuPage.editor.size.paused') }}
    </p>
    <p v-if="titleError" class="size-error">{{ titleError }}</p>
    <p v-if="priceError" class="size-error">{{ priceError }}</p>

    <div v-if="row.discountText !== null" class="size-discount">
      <span class="discount-label">{{
        t('menuPage.editor.discountPrice')
      }}</span>
      <div class="discount-input">
        <PosUiInput
          :value="row.discountText"
          :prefix="currencySymbol"
          inputmode="decimal"
          autocomplete="off"
          :aria-label="t('menuPage.editor.discountPrice')"
          :disabled="readOnly"
          @input="emit('change', { discountText: $event })"
        />
      </div>
    </div>
    <p v-if="discountError" class="size-error">{{ discountError }}</p>

    <div v-if="detailsOpen" class="size-details">
      <V2MenuPhotoSlot
        class="details-photo"
        compact
        :value="row.picture"
        :uploading="uploading"
        :disabled="readOnly"
        @pick="pickPhoto"
        @remove="emit('change', { picture: null })"
      />
      <PosUiTextarea
        class="details-description"
        :value="row.description || ''"
        :placeholder="t('menuPage.editor.size.descriptionPlaceholder')"
        :auto-grow="{ minRows: 2, maxRows: 5 }"
        :disabled="readOnly"
        @input="emit('change', { description: $event })"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-size-row {
    & + .v2-catalog-size-row {
      @apply tw-mt-2;
    }

    .size-main {
      @apply tw-flex tw-items-center;

      .size-title {
        @apply tw-min-w-0 tw-flex-1;
      }

      .size-price {
        @apply tw-ml-2 tw-w-32 tw-flex-none;
      }

      .size-more {
        @apply tw-ml-1 tw-flex-none;
      }

      .more-trigger {
        @apply tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-xl tw-text-neutral-500;

        &:hover {
          @apply tw-bg-neutral-100 tw-text-neutral-800;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }

      .more-list {
        width: 230px;
      }
    }

    &.is-paused .size-main {
      .size-title,
      .size-price {
        opacity: 0.6;
      }
    }

    .size-paused {
      @apply tw-m-0 tw-mt-1 tw-text-xs tw-font-medium tw-text-neutral-500;
    }

    .size-error {
      @apply tw-m-0 tw-mt-1 tw-text-xs tw-text-red-600;
    }

    .size-discount {
      @apply tw-mt-2 tw-flex tw-items-center tw-pr-12;

      .discount-label {
        @apply tw-min-w-0 tw-flex-1 tw-text-right tw-text-sm tw-text-neutral-600;
      }

      .discount-input {
        @apply tw-ml-2 tw-w-32 tw-flex-none;
      }
    }

    .size-details {
      @apply tw-mt-2 tw-flex tw-items-start tw-rounded-lg tw-bg-canvas tw-p-3;

      .details-photo {
        @apply tw-mr-3 tw-flex-none;
      }

      .details-description {
        @apply tw-min-w-0 tw-flex-1;
      }
    }
  }
</style>
